import { ACCOUNT_TYPE } from '$pages/GlobalDashboardUser/constants'
import {
  getAppsToAssign,
  getGlobalDashboardUserAssignedApps,
  getGlobalDashboardUserDetail,
  getGlobalDashboardUserList,
  getUserRoleSelect,
  getUserTypeSelect,
  postAppsToAssign,
  postGlobalDashboardUserDetail,
  putGlobalDashboardUserDetail
} from '$services/api'
import { useRequest } from 'ahooks'
import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'

const SERVICE_ROLE = 'service'

export const useGlobalDashboardUserService = () => {
  const {
    run: fetchGlobalDashboardUserList,
    runAsync: fetchGlobalDashboardUserListAsync,
    data: GlobalDashboardUserListData,
    loading: fetchingGlobalDashboardUserList
  } = useRequest(getGlobalDashboardUserList, {
    manual: true
  })

  const {
    run: fetchGlobalDashboardUserDetail,
    runAsync: fetchGlobalDashboardUserDetailAsync,
    data: globalDashboardUserDetail,
    loading: fetchingGlobalDashboardUserDetail
  } = useRequest(getGlobalDashboardUserDetail, {
    manual: true
  })

  const {
    run: fetchGlobalDashboardUserAssignedApps,
    runAsync: fetchGlobalDashboardUserAssignedAppsAsync,
    data: globalDashboardUserAssignedApps,
    loading: fetchingGlobalDashboardUserAssignedApps
  } = useRequest(getGlobalDashboardUserAssignedApps, {
    manual: true
  })

  const { data: userRoleSelectData, run: fetchUserRoleSelect } = useRequest(
    getUserRoleSelect,
    { manual: true }
  )

  const { data: userTypeSelectData, run: fetchUserTypeSelect } = useRequest(
    getUserTypeSelect,
    { manual: true }
  )

  const {
    runAsync: createGlobalDashboardUserAsync,
    loading: creatingGlobalDashboardUser
  } = useRequest(postGlobalDashboardUserDetail, {
    manual: true,
    onSuccess: () => {
      toast.success('Successfully Created!')
    }
  })

  const {
    runAsync: updateGlobalDashboardUserAsync,
    loading: updatingGlobalDashboardUser
  } = useRequest(putGlobalDashboardUserDetail, {
    manual: true,
    onSuccess: () => {
      toast.success('Successfully Updated!')
    }
  })

  const {
    run: fetchAppsToAssign,
    loading: fetchingAppsToAssign,
    data: appsToAssignData
  } = useRequest(getAppsToAssign, { manual: true })

  const { runAsync: attachAppsToUserAsync, loading: attachingAppsToUser } =
    useRequest(postAppsToAssign, { manual: true })

  const { data: list = [], meta } = { ...GlobalDashboardUserListData }

  const { data: appsToAssignList = [], meta: appsToAssignMeta } =
    appsToAssignData ?? {}

  const user_roles_options = useMemo(
    () =>
      userRoleSelectData?.data.map(v => ({
        label: v.display_name,
        value: v.name
      })) ?? [],
    [userRoleSelectData?.data]
  )

  const filter_user_roles_options = useCallback(
    (accountType: string) => {
      if (!accountType) return []

      const isAdminType = accountType === ACCOUNT_TYPE.ADMIN

      return user_roles_options.filter(d => {
        const isServiceRole = d.value.includes(SERVICE_ROLE)
        return isAdminType ? !isServiceRole : isServiceRole
      })
    },
    [user_roles_options]
  )

  const user_types_options =
    userTypeSelectData?.data.map(v => ({
      label: v.display_name,
      value: v.name
    })) ?? []

  const assignedApps = globalDashboardUserAssignedApps?.data ?? []

  return {
    fetchGlobalDashboardUserList,
    fetchGlobalDashboardUserListAsync,
    fetchingGlobalDashboardUserList,
    dashboardUsers: { list, meta },

    fetchGlobalDashboardUserDetail,
    fetchGlobalDashboardUserDetailAsync,
    dashboardUser: globalDashboardUserDetail?.data,
    fetchingGlobalDashboardUserDetail,

    fetchGlobalDashboardUserAssignedApps,
    fetchGlobalDashboardUserAssignedAppsAsync,
    assignedApps,
    fetchingGlobalDashboardUserAssignedApps,

    fetchUserRoleSelect,
    fetchUserTypeSelect,
    user_roles_options,
    user_types_options,
    filter_user_roles_options,

    updateGlobalDashboardUserAsync,
    updatingGlobalDashboardUser,

    createGlobalDashboardUserAsync,
    creatingGlobalDashboardUser,

    appsToAssign: { list: appsToAssignList, meta: appsToAssignMeta },
    fetchingAppsToAssign,
    fetchAppsToAssign,

    attachAppsToUserAsync,
    attachingAppsToUser
  }
}
